import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { GoogleLogo } from "@phosphor-icons/react";

export default function Auth() {
  const [params] = useSearchParams();
  const [mode, setMode] = useState("login");
  const [role, setRole] = useState(params.get("role") || "buyer");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const { setUser } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const path = mode === "login" ? "/auth/login" : "/auth/register";
      const payload = mode === "login"
        ? { email: form.email, password: form.password }
        : { ...form, role };
      const { data } = await api.post(path, payload);
      setUser(data.user);
      toast.success("Bienvenue !");
      navigate(data.user.role === "admin" ? "/admin" : data.user.role === "seller" ? "/dashboard" : "/catalog");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    sessionStorage.setItem("pending_role", role);
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative bg-slate-900">
        <img src="https://images.unsplash.com/photo-1782392477697-e7d243aa2d36?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000" alt="" className="w-full h-full object-cover opacity-60" />
        <div className="absolute inset-0 p-12 flex flex-col justify-end">
          <h2 className="font-display text-4xl font-bold text-white text-shadow-soft">L'immobilier, réinventé pour l'Afrique de l'Ouest.</h2>
          <p className="text-stone-200 mt-3 text-shadow-soft">Vente, location, événementiel & analyse patrimoniale IA.</p>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12 bg-stone-50">
        <div className="w-full max-w-md">
          <Link to="/" className="inline-block mb-8">
            <img src="/lomar-logo.png" alt="Lomar Properties" className="h-16 w-auto rounded-xl" />
          </Link>
          <h1 className="font-display text-3xl font-bold text-stone-900">{mode === "login" ? "Connexion" : "Créer un compte"}</h1>
          <p className="text-stone-500 mt-1 text-sm">{mode === "login" ? "Accédez à votre espace." : "Rejoignez la plateforme."}</p>

          {mode === "register" && (
            <div className="grid grid-cols-2 gap-3 mt-6">
              {[["buyer", "Acheteur / Locataire"], ["seller", "Vendeur / Propriétaire"]].map(([r, l]) => (
                <button key={r} type="button" data-testid={`role-tab-${r}`} onClick={() => setRole(r)}
                  className={`text-sm font-semibold rounded-xl border-2 p-3 transition-colors ${role === r ? "border-orange-800 bg-orange-50 text-orange-800" : "border-stone-200 text-stone-500"}`}>
                  {l}
                </button>
              ))}
            </div>
          )}

          <form onSubmit={submit} className="mt-6 space-y-4">
            {mode === "register" && (
              <div>
                <Label>Nom complet</Label>
                <Input data-testid="auth-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="mt-1" placeholder="Amadou Diallo" />
              </div>
            )}
            <div>
              <Label>Email</Label>
              <Input data-testid="auth-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required className="mt-1" placeholder="vous@email.com" />
            </div>
            <div>
              <Label>Mot de passe</Label>
              <Input data-testid="auth-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required className="mt-1" placeholder="••••••••" />
            </div>
            <Button data-testid="auth-submit" type="submit" disabled={loading} className="w-full rounded-full bg-orange-800 hover:bg-orange-900 text-white h-11">
              {loading ? "…" : mode === "login" ? "Se connecter" : "Créer mon compte"}
            </Button>
          </form>

          <div className="flex items-center gap-3 my-5 text-stone-400 text-xs">
            <div className="flex-1 h-px bg-stone-200" /> OU <div className="flex-1 h-px bg-stone-200" />
          </div>
          <Button data-testid="google-login-btn" variant="outline" onClick={googleLogin} className="w-full rounded-full h-11 border-stone-300">
            <GoogleLogo weight="bold" size={20} className="mr-2" /> Continuer avec Google
          </Button>

          <p className="text-center text-sm text-stone-500 mt-6">
            {mode === "login" ? "Pas de compte ?" : "Déjà inscrit ?"}{" "}
            <button data-testid="auth-toggle" onClick={() => setMode(mode === "login" ? "register" : "login")} className="text-orange-800 font-semibold">
              {mode === "login" ? "S'inscrire" : "Se connecter"}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
